import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Box, Home, Building2, ShoppingBag, Warehouse, Car, MoreHorizontal } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UNIT_FUNCTIE_OPTIONS = [
  { value: 'Wonen', label: 'Wonen', icon: Home },
  { value: 'Kantoor', label: 'Kantoor', icon: Building2 },
  { value: 'Retail', label: 'Retail', icon: ShoppingBag },
  { value: 'Logistiek', label: 'Logistiek', icon: Warehouse },
  { value: 'Parkeren', label: 'Parkeren', icon: Car },
  { value: 'Overig', label: 'Overig', icon: MoreHorizontal }
];

const UNIT_STATUS_OPTIONS = [
  { value: 'Verhuurd', label: 'Verhuurd', color: 'bg-green-500' },
  { value: 'Leeg', label: 'Leeg', color: 'bg-red-500' },
  { value: 'In renovatie', label: 'In renovatie', color: 'bg-yellow-500' },
  { value: 'Onbekend', label: 'Onbekend', color: 'bg-slate-500' }
];

export const UnitsTab = ({ buildingId, onUnitsChange, t }) => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [form, setForm] = useState({
    unit_naam: '',
    unit_functie: 'Overig',
    unit_oppervlakte_m2: '',
    unit_verdieping: '',
    unit_status: 'Onbekend'
  });

  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/buildings/${buildingId}/units`, {
        withCredentials: true
      });
      setUnits(res.data);
      if (onUnitsChange) onUnitsChange(res.data);
    } catch (err) {
      console.error('Error fetching units:', err);
      toast.error('Fout bij ophalen units');
    } finally {
      setLoading(false);
    }
  }, [buildingId, onUnitsChange]);

  useEffect(() => {
    if (buildingId) {
      fetchUnits();
    }
  }, [buildingId, fetchUnits]);

  const resetForm = () => {
    setForm({
      unit_naam: '',
      unit_functie: 'Overig',
      unit_oppervlakte_m2: '',
      unit_verdieping: '',
      unit_status: 'Onbekend'
    });
    setEditingUnit(null);
  };

  const openDialog = (unit = null) => {
    if (unit) {
      setEditingUnit(unit);
      setForm({
        unit_naam: unit.unit_naam || '',
        unit_functie: unit.unit_functie || 'Overig',
        unit_oppervlakte_m2: unit.unit_oppervlakte_m2 || '',
        unit_verdieping: unit.unit_verdieping || '',
        unit_status: unit.unit_status || 'Onbekend'
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    

    const payload = {
      ...form,
      building_id: buildingId,
      unit_oppervlakte_m2: form.unit_oppervlakte_m2 ? parseFloat(form.unit_oppervlakte_m2) : null
    };

    try {
      if (editingUnit) {
        await axios.put(`${API_URL}/api/units/${editingUnit.unit_id}`, payload, {
          withCredentials: true
        });
        toast.success('Unit bijgewerkt');
      } else {
        await axios.post(`${API_URL}/api/buildings/${buildingId}/units`, payload, {
          withCredentials: true
        });
        toast.success('Unit toegevoegd');
      }
      setDialogOpen(false);
      resetForm();
      fetchUnits();
    } catch (err) {
      console.error('Error saving unit:', err);
      toast.error('Fout bij opslaan unit');
    }
  };

  const handleDelete = async (unitId) => {
    if (!window.confirm('Weet je zeker dat je deze unit wilt verwijderen? Alle gekoppelde huurcontracten worden ook verwijderd.')) {
      return;
    }
    
    try {
      
      await axios.delete(`${API_URL}/api/units/${unitId}`, {
        withCredentials: true
      });
      toast.success('Unit verwijderd');
      fetchUnits();
    } catch (err) {
      console.error('Error deleting unit:', err);
      toast.error('Fout bij verwijderen unit');
    }
  };

  const getFunctieIcon = (functie) => {
    const opt = UNIT_FUNCTIE_OPTIONS.find(o => o.value === functie);
    if (opt) {
      const Icon = opt.icon;
      return <Icon className="h-4 w-4" />;
    }
    return <Box className="h-4 w-4" />;
  };

  const getStatusBadge = (status) => {
    const opt = UNIT_STATUS_OPTIONS.find(o => o.value === status);
    return (
      <Badge className={`${opt?.color || 'bg-slate-500'} text-white`}>
        {status}
      </Badge>
    );
  };

  const totalSqm = units.reduce((sum, u) => sum + (u.unit_oppervlakte_m2 || 0), 0);

  return (
    <div className="space-y-4" data-testid="units-tab">
      {/* Summary */}
      <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-4">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-slate-400 text-sm">Totaal Units</span>
            <p className="text-2xl font-bold text-white">{units.length}</p>
          </div>
          <div className="border-l border-slate-700 pl-6">
            <span className="text-slate-400 text-sm">Totaal m²</span>
            <p className="text-2xl font-bold text-white">{totalSqm.toLocaleString('nl-NL')}</p>
          </div>
          <div className="border-l border-slate-700 pl-6">
            <span className="text-slate-400 text-sm">Verhuurd</span>
            <p className="text-2xl font-bold text-green-400">
              {units.filter(u => u.unit_status === 'Verhuurd').length}
            </p>
          </div>
          <div className="border-l border-slate-700 pl-6">
            <span className="text-slate-400 text-sm">Leeg</span>
            <p className="text-2xl font-bold text-red-400">
              {units.filter(u => u.unit_status === 'Leeg').length}
            </p>
          </div>
        </div>
        <Button 
          onClick={() => openDialog()} 
          className="bg-[#b4976f] hover:bg-[#a3865f] text-white"
          data-testid="add-unit-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Unit Toevoegen
        </Button>
      </div>

      {/* Units Table */}
      {loading && units.length === 0 ? (
        <div className="text-center py-8 text-slate-400">Laden...</div>
      ) : units.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/30 rounded-lg border border-dashed border-slate-700">
          <Box className="h-12 w-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 mb-4">Nog geen units geregistreerd</p>
          <Button 
            onClick={() => openDialog()}
            variant="outline"
            className="border-[#b4976f] text-[#b4976f] hover:bg-[#b4976f]/10"
          >
            <Plus className="h-4 w-4 mr-2" />
            Eerste Unit Toevoegen
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-800 hover:bg-slate-800">
                <TableHead className="text-slate-300">Unit Naam</TableHead>
                <TableHead className="text-slate-300">Functie</TableHead>
                <TableHead className="text-slate-300 text-right">Oppervlakte (m²)</TableHead>
                <TableHead className="text-slate-300">Verdieping</TableHead>
                <TableHead className="text-slate-300">Status</TableHead>
                <TableHead className="text-slate-300 text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map((unit) => (
                <TableRow key={unit.unit_id} className="border-slate-700 hover:bg-slate-800/50">
                  <TableCell className="text-white font-medium">
                    <div className="flex items-center gap-2">
                      {getFunctieIcon(unit.unit_functie)}
                      {unit.unit_naam}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300">{unit.unit_functie}</TableCell>
                  <TableCell className="text-slate-300 text-right">
                    {unit.unit_oppervlakte_m2?.toLocaleString('nl-NL') || '-'}
                  </TableCell>
                  <TableCell className="text-slate-300">{unit.unit_verdieping || '-'}</TableCell>
                  <TableCell>{getStatusBadge(unit.unit_status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDialog(unit)}
                        className="text-slate-400 hover:text-white"
                        data-testid={`edit-unit-${unit.unit_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(unit.unit_id)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-unit-${unit.unit_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Unit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingUnit ? 'Unit Bewerken' : 'Nieuwe Unit'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Unit Naam *</Label>
              <Input
                value={form.unit_naam}
                onChange={(e) => setForm({ ...form, unit_naam: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="Bijv. Unit 1A, Begane grond"
                required
                data-testid="unit-naam-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Functie *</Label>
                <Select value={form.unit_functie} onValueChange={(v) => setForm({ ...form, unit_functie: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="unit-functie-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {UNIT_FUNCTIE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-800">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Status *</Label>
                <Select value={form.unit_status} onValueChange={(v) => setForm({ ...form, unit_status: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="unit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {UNIT_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-slate-800">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Oppervlakte (m²)</Label>
                <Input
                  type="number"
                  value={form.unit_oppervlakte_m2}
                  onChange={(e) => setForm({ ...form, unit_oppervlakte_m2: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="0"
                  data-testid="unit-sqm-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Verdieping</Label>
                <Input
                  value={form.unit_verdieping}
                  onChange={(e) => setForm({ ...form, unit_verdieping: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  placeholder="Bijv. BG, 1, -1"
                  data-testid="unit-verdieping-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600 text-slate-300">
                Annuleren
              </Button>
              <Button type="submit" className="bg-[#b4976f] hover:bg-[#a3865f] text-white" data-testid="save-unit-btn">
                {editingUnit ? 'Bijwerken' : 'Toevoegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnitsTab;
